import { useState } from 'react';
import { X, Check, Eye, Upload, Image, Headphones, AlertCircle, CheckCircle } from 'lucide-react';
import { useUserAssets } from '../../hooks/useUserAssets';
import { uploadFile } from '../../utils/supabaseStorage';
import { useAppStore } from '../../store/useAppStore';
import { CachedImage } from '../CachedImage';
import { VideoThumbnail } from '../VideoThumbnail';
import { LoadingSpinner } from '../LoadingSpinner';
import { MediaPreviewModal } from '../MediaPreviewModal';
import { getAssetMediaType } from '../../utils/formPreviewHelpers';

interface AssetsGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAsset: (assets: any[]) => void;
  multiple?: boolean;
  maxSelections?: number;
  fileLimit?: number;
  /** Page app id for uploads (Page Settings). */
  appId?: string;
}

export const AssetsGalleryModal = ({
  isOpen,
  onClose,
  onSelectAsset,
  multiple = false,
  maxSelections = 10,
  fileLimit,
  appId = '',
}: AssetsGalleryModalProps) => {
  const { session } = useAppStore();

  const actualMaxSelections = fileLimit || maxSelections;

  const [selectedAssets, setSelectedAssets] = useState<Record<string, any>>({});
  const [previewAsset, setPreviewAsset] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'upload' | 'generated'>('upload');

  const { assets, loading, error, refetch } = useUserAssets();

  // Filter assets based on type and search
  const filteredAssets = assets.filter(asset => {
    // Filter based on upload/generated status
    if (activeFilter === 'upload') {
      return !asset.isaigenerated;
    } else if (activeFilter === 'generated') {
      return asset.isaigenerated;
    }
    return true;
  });

  const isSelected = (assetId: string): boolean => {
    return !!selectedAssets[assetId];
  };

  const handleAssetToggle = (asset: any) => {
    setSelectedAssets(prev => {
      const newMap = { ...prev };

      if (newMap[asset.id]) {
        delete newMap[asset.id];
      } else {
        if (multiple) {
          if (Object.keys(newMap).length < actualMaxSelections) {
            newMap[asset.id] = asset;
          }
        } else {
          Object.keys(newMap).forEach(key => delete newMap[key]);
          newMap[asset.id] = asset;
        }
      }

      return newMap;
    });
  };

  const handleConfirmSelection = () => {
    const selectedAssetsList = Object.values(selectedAssets);
    console.log('🎯 [ASSET-SELECTION] Confirming selection:', selectedAssetsList);
    console.log('🎯 [ASSET-SELECTION] Multiple mode:', multiple);
    console.log('🎯 [ASSET-SELECTION] Selected count:', selectedAssetsList.length);

    if (selectedAssetsList.length > 0) {
      console.log('✅ [ASSET-SELECTION] Processing selection and closing modal');

      // Call the selection callback immediately
      onSelectAsset(selectedAssetsList);
    console.log('✅ [ASSET-SELECTION] Calling onSelectAsset with assets:', selectedAssetsList.map(a => ({ id: a.id, filename: a.filename })));

    try {
      // Call the selection callback and wait for it to complete
      onSelectAsset(selectedAssetsList);
      console.log('✅ [ASSET-SELECTION] onSelectAsset called successfully');

      // Use setTimeout to ensure the parent component processes the selection first
      setTimeout(() => {
        console.log('🚪 [ASSET-SELECTION] Closing modal after selection processed');
        setSelectedAssets({});
        onClose();
      }, 100);

    } catch (error) {
      console.error('❌ [ASSET-SELECTION] Error in onSelectAsset:', error);
    }
    }
  };

  const handleCancel = () => {
    console.log('❌ [ASSET-SELECTION] User cancelled asset selection');

    // Clear any selected assets
    setSelectedAssets({});

    // Close modal without calling onSelectAsset
    onClose();

    console.log('🚪 [ASSET-SELECTION] Modal cancelled and closed');
  };


  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    if (!session?.hy_user_id) {
      setUploadError('User session not found. Please log in.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach(file => {
        const isValidType = validateFileType(file, activeFilter);
        if (isValidType) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        setUploadError(`Invalid file types: ${invalidFiles.join(', ')}. Please select ${activeFilter} files only.`);
        setIsUploading(false);
        return;
      }

      if (validFiles.length === 0) {
        setUploadError('No valid files selected.');
        setIsUploading(false);
        return;
      }

      let successCount = 0;
      const errors: string[] = [];
      const uploadedAssets: any[] = [];

      const uploadAppId = appId?.trim() ?? '';
      if (!uploadAppId) {
        setUploadError('App ID missing — set it in Page Settings for this page.');
        setIsUploading(false);
        return;
      }

      for (const file of validFiles) {
        try {
          const result = await uploadFile(
            file,
            session.hy_user_id,
            'uploaded',
            false,
            uploadAppId
          );

          if (result.success) {
            successCount++;
            // Create asset object for auto-selection
            uploadedAssets.push({
              id: result.assetId,
              public_url: result.publicUrl,
              filename: file.name,
              file_size: file.size,
              media_type: file.type.startsWith('image/') ? 'image' :
                         file.type.startsWith('video/') ? 'video' : 'audio',
              file_extension: file.name.split('.').pop()?.toLowerCase(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              hy_user_id: session.hy_user_id,
              isaigenerated: false
            });
          } else {
            errors.push(`${file.name}: ${result.error || 'Upload failed'}`);
          }
        } catch (err) {
          errors.push(`${file.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      }

      if (successCount > 0) {
        setUploadSuccess(`Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}`);
        await refetch();
        window.dispatchEvent(new CustomEvent('refreshAssetGallery'));

        // Auto-select uploaded assets
        if (uploadedAssets.length > 0) {
          const newSelectedAssets: Record<string, any> = {};

          if (multiple) {
            // For multiple selection, add to existing selection up to limit
            const currentSelectionCount = Object.keys(selectedAssets).length;
            const remainingSlots = actualMaxSelections - currentSelectionCount;
            const assetsToSelect = uploadedAssets.slice(0, remainingSlots);

            // Keep existing selections and add new ones
            Object.assign(newSelectedAssets, selectedAssets);
            assetsToSelect.forEach(asset => {
              newSelectedAssets[asset.id] = asset;
            });
          } else {
            // For single selection, select the first uploaded asset
            newSelectedAssets[uploadedAssets[0].id] = uploadedAssets[0];
          }

          setSelectedAssets(newSelectedAssets);
          console.log('✅ [AUTO-SELECT] Auto-selected uploaded assets:', Object.keys(newSelectedAssets));
        }
      }

      if (errors.length > 0) {
        setUploadError(`Some uploads failed: ${errors.join('; ')}`);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const validateFileType = (file: File, filterType: string): boolean => {
    const fileType = file.type.toLowerCase();
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    switch (filterType) {
      case 'image':
        return fileType.startsWith('image/') ||
               ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension);

      case 'video':
        return fileType.startsWith('video/') ||
               ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension);

      case 'audio':
        return fileType.startsWith('audio/') ||
               ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension);

      case 'all':
      default:
        return true;
    }
  };

  const getAcceptAttribute = (filterType: string): string => {
    switch (filterType) {
      case 'image':
        return 'image/*,.jpg,.jpeg,.png,.gif,.webp,.svg,.bmp';
      case 'video':
        return 'video/*,.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv';
      case 'audio':
        return 'audio/*,.mp3,.wav,.flac,.aac,.ogg,.m4a';
      default:
        return '*/*';
    }
  };

  const renderAssetPreview = (asset: any) => {
    const mediaType = getAssetMediaType(asset);

    switch (mediaType) {
      case 'image':
        return (
          <CachedImage
            src={asset.public_url}
            alt="Asset"
            className="w-full h-full object-cover"
            fallback={
              <div className="w-full h-full flex items-center justify-center text-th-secondary bg-elevated">
                <Image className="w-6 h-6" />
              </div>
            }
          />
        );

      case 'video':
        return (
          <div className="w-full h-full">
            <VideoThumbnail
              src={asset.public_url}
              className="w-full h-full"
              showPlayButton={true}
              onClick={() => setPreviewAsset(asset)}
            />
          </div>
        );

      case 'audio':
        return (
          <div className="w-full h-full bg-status-warning/20 flex items-center justify-center">
            <Headphones className="w-6 h-6 text-status-warning" />
          </div>
        );

      default:
        return (
          <div className="w-full h-full bg-elevated flex items-center justify-center">
            <Image className="w-6 h-6 text-th-secondary" />
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-base z-dropdown flex flex-col">
      {/* Native-style Header */}
      <div className="bg-surface px-4 py-3 border-b border-subtle/50">
        {/* Upload/Generated Filter Chips */}
        <div className="flex items-center justify-center space-x-3 mb-4">
          <button
            onClick={() => setActiveFilter('upload')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
              activeFilter === 'upload'
                ? 'bg-accent text-surface shadow-lg'
                : 'bg-elevated/50 text-th-secondary hover:text-th-primary hover:bg-elevated border border-subtle/30'
            }`}
          >
            <Upload className="w-4 h-4" />
            <span>Upload</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
              activeFilter === 'upload'
                ? 'bg-white/20 text-white'
                : 'bg-accent/20 text-accent'
            }`}>
              {assets.filter(a => !a.isaigenerated).length}
            </span>
          </button>

          <button
            onClick={() => setActiveFilter('generated')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
              activeFilter === 'generated'
                ? 'bg-brand text-surface shadow-lg'
                : 'bg-elevated/50 text-th-secondary hover:text-th-primary hover:bg-elevated border border-subtle/30'
            }`}
          >
            <Image className="w-4 h-4" />
            <span>Generated</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
              activeFilter === 'generated'
                ? 'bg-white/20 text-white'
                : 'bg-brand/20 text-brand'
            }`}>
              {assets.filter(a => a.isaigenerated).length}
            </span>
          </button>
        </div>

        {/* Upload Button */}
        <div className="flex justify-center">
          <input
            type="file"
            multiple
            accept={getAcceptAttribute(activeFilter)}
            onChange={handleFileUpload}
            className="hidden"
            id="upload-files-input"
            disabled={isUploading}
          />
          <label
            htmlFor="upload-files-input"
            className={`w-full flex items-center justify-center space-x-3 px-8 py-4 bg-brand text-surface font-semibold transition-all shadow-lg ${
              isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-[0.98]'
            }`}
          >
            {isUploading ? (
              <LoadingSpinner size="sm" className="text-white" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
            <span className="text-lg">Upload</span>
          </label>
        </div>
      </div>

      {/* Status Messages */}
      {(uploadSuccess || uploadError) && (
        <div className="px-4 py-2 border-b border-subtle/50">
          {uploadSuccess && (
            <div className="p-3 bg-status-success/10 border border-status-success/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-status-success" />
                  <span className="text-status-success font-medium text-sm">{uploadSuccess}</span>
                </div>
                <button
                  onClick={() => setUploadSuccess(null)}
                  className="text-status-success hover:text-status-success/80"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {uploadError && (
            <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-status-error" />
                  <span className="text-status-error text-sm">{uploadError}</span>
                </div>
                <button
                  onClick={() => setUploadError(null)}
                  className="text-status-error hover:text-status-error/80"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-4">
              <LoadingSpinner size="lg" />
              <p className="text-th-secondary">Loading your assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center space-y-4 max-w-sm">
              <div className="text-status-error text-6xl">⚠️</div>
              <h3 className="text-xl font-bold text-th-primary">Failed to Load Assets</h3>
              <p className="text-th-secondary text-sm">{error}</p>
              <button onClick={refetch} className="button-primary">
                Try Again
              </button>
            </div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center space-y-4 max-w-sm">
              <div className="text-6xl mb-4">📁</div>
              <h3 className="text-xl font-bold text-th-primary">
                No Assets Found
              </h3>
              <p className="text-th-secondary">
                No {activeFilter} assets in your gallery
              </p>
              <label
                htmlFor="upload-files-input"
                className="button-primary inline-flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Upload Files</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="p-4 pb-32">
            <div className="p-4">
              {/* Native-style Grid */}
              <div className="columns-2 md:columns-3 gap-4 space-y-4">
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="relative break-inside-avoid mb-4"
                  >
                    {/* Asset Container */}
                    <div className={`relative w-full rounded-xl overflow-hidden transition-all ${
                      isSelected(asset.id)
                        ? 'ring-4 ring-brand shadow-lg shadow-brand/50 transform scale-95'
                        : 'hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                    onClick={() => handleAssetToggle(asset)}
                    >
                      {/* Asset Preview */}
                      <div className="w-full bg-elevated">
                        {renderAssetPreview(asset)}
                      </div>

                      {/* Selection Overlay */}
                      <div className={`absolute inset-0 transition-all ${
                        isSelected(asset.id)
                          ? 'bg-brand/30 border-2 border-brand'
                          : 'bg-transparent hover:bg-black/20 active:bg-black/30'
                      }`}>
                        {/* Large Selection Indicator */}
                        <div className="absolute top-3 right-3">
                          {isSelected(asset.id) ? (
                            <div className="w-8 h-8 bg-brand text-surface rounded-full flex items-center justify-center shadow-lg border-2 border-surface">
                              <Check className="w-5 h-5 font-bold" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-black/60 backdrop-blur-sm border-2 border-white/80 rounded-full flex items-center justify-center hover:bg-black/80 transition-all">
                              <div className="w-3 h-3 bg-white/60 rounded-full" />
                            </div>
                          )}
                        </div>

                        {/* Selected Badge */}
                        {isSelected(asset.id) && (
                          <div className="absolute top-3 left-3">
                            <div className="px-3 py-1 bg-brand text-surface rounded-full text-xs font-bold shadow-lg">
                              SELECTED
                            </div>
                          </div>
                        )}

                        {/* Preview Button */}
                        <div className={`absolute ${isSelected(asset.id) ? 'bottom-3 left-3' : 'top-3 left-3'}`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewAsset(asset);
                            }}
                            className="w-8 h-8 bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/80 transition-all border border-white/30"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Media Type Badge */}
                        <div className="absolute bottom-3 right-3">
                          <div className="px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full border border-white/30">
                            <span className="text-white text-xs font-medium uppercase">
                              {getAssetMediaType(asset)}
                            </span>
                          </div>
                        </div>

                        {/* Tap to Select Hint for unselected assets */}
                        {!isSelected(asset.id) && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <div className="px-4 py-2 bg-black/80 backdrop-blur-sm text-white rounded-lg text-sm font-medium">
                              Tap to Select
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Native-style Bottom Action Bar */}
      <div className="bg-surface border-t border-subtle safe-area-bottom sticky bottom-0 z-toast">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-th-secondary">
              {multiple ? (
                `${Object.keys(selectedAssets).length}/${actualMaxSelections} selected`
              ) : (
                Object.keys(selectedAssets).length > 0 ? '1 selected' : 'Tap to select'
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleCancel}
                className="px-8 py-4 bg-elevated border border-subtle text-th-secondary rounded-xl font-semibold hover:text-th-primary hover:border-brand/50 transition-all active:scale-[0.98] touch-manipulation min-h-[52px] text-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSelection}
                disabled={Object.keys(selectedAssets).length === 0}
                className="flex-1 px-8 py-4 bg-brand text-surface rounded-xl font-semibold hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98] shadow-lg touch-manipulation min-h-[52px] text-lg"
              >
                {multiple ? 'Select Assets' : 'Select Asset'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      {previewAsset && (
        <MediaPreviewModal
          isOpen={!!previewAsset}
          onClose={() => setPreviewAsset(null)}
          asset={previewAsset}
          onDownload={() => {
            const link = document.createElement('a');
            link.href = previewAsset.public_url;
            link.download = previewAsset.filename || `asset-${previewAsset.id}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />
      )}
    </div>
  );
};
